import { KeyValue } from '@angular/common';
import { Component, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbModal, NgbModalRef, NgbTypeahead } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { AuthService } from '../../../auth/auth.service';
import { UserDto } from '../../../auth/user.dto';
import { ConfirmationComponent } from '../../../shared/confirmation/confirmation.component';
import { DisplayLabel } from '../../../shared/models/displayLabel';
import { AddressType } from '../../../shared/models/enums/addressType.enum';
import { AttachedOrganization, labelsByOrganization } from '../../../shared/models/enums/attached-organization.enum';
import { DomiciliationType, DomiciliationTypeLabel, labelsByDomiciliationType } from '../../../shared/models/enums/domiciliation-type.enum';
import { FolderStatus, labelsByFolderStatus, labelsByFolderStatusKey } from '../../../shared/models/enums/folderStatus.enum';
import { MaritalStatus, labelsByMaritalStatus } from '../../../shared/models/enums/marital-status.enum';
import { Nationality, labelsByNationality } from '../../../shared/models/enums/nationality.enum';
import { OrientedBy, labelsByOrientedBy } from '../../../shared/models/enums/oriented-by.enum';
import { ResidentialStatus, ResidentialStatusLabel, labelsByResidentialStatus } from '../../../shared/models/enums/residential-status.enum';
import { SupportTypes } from '../../../shared/models/enums/support-type.enum';
import { Title } from '../../../shared/models/enums/title.enum';
import { TownDto } from '../../../shared/models/town.dto';
import { AreaService } from '../../../shared/services/area.service';
import { BeneficiariesService } from '../../../shared/services/beneficiaries.service';
import { BeneficiaryComplementsService } from '../../../shared/services/beneficiaryComplements.service';
import { DateService } from '../../../shared/services/date.service';
import { HealthInsurancesService } from '../../../shared/services/health-insurances.service';
import { QPVService } from '../../../shared/services/qpv.service.ts';
import { StringUtils } from '../../../shared/services/stringUtils.service';
import { SupportStatusTypeService } from '../../../shared/services/support-status-type.service';
import { SupportTypeService } from '../../../shared/services/supportType.service';
import { TownService } from '../../../shared/services/town.service';
import { SortableDirective } from '../../../shared/sortable/sortable.directive';
import { ToastService } from '../../../shared/toast/toast.service';
import { Utils } from '../../../shared/utils';
import { DateValidators } from '../../../shared/validators/date.validators';
import { BeneficiaryBaseComponent } from '../../beneficiary-base.component';
import { BeneficiaryDuplicateCheckComponent } from '../../beneficiary-duplicate-check/beneficiary-duplicate-check.component';
import { BeneficiaryDuplicateCheckDto } from '../../beneficiary-duplicate-check/beneficiary-duplicate-check.dto';
import { BeneficiaryAddressDto } from '../../dto/beneficiary-address.dto';
import { BeneficiaryDto } from '../../dto/beneficiary.dto';
import { SupportDto } from '../../dto/supportDto';
import { SupportTypeDto } from '../../dto/supportTypeDto';
import { BeneficiaryComplement } from '../complement/beneficiaryComplement';
import { BeneficiaryComplementSaveDto } from '../complement/beneficiaryComplementSaveDto';

type addressFormType = {
  id: FormControl<number | null>;
  beginDate: FormControl<Date | null>;
  endDate: FormControl<Date | null>;
  address: FormControl<string | null>;
  additionalAddress: FormControl<string | null>;
  postalCode: FormControl<string | null>;
  city: FormControl<string | null>;
  qpvId: FormControl<number | null>;
  cliId: FormControl<number | null>;
  ctmLabel: FormControl<string | null>;
  residentialStatus: FormControl<string | null>;
  residentialStatusComplement: FormControl<string | null>;
};

type postalAddressForm = {
  id: FormControl<number | null>;
  postalBeginDate: FormControl<Date | null>;
  postalEndDate: FormControl<Date | null>;
  postalAddress: FormControl<string | null>;
  postalAdditionalAddress: FormControl<string | null>;
  postalPostalCode: FormControl<string | null>;
  postalCity: FormControl<string | null>;
  domiciliationType: FormControl<string | null>;
  domiciliationTypeComplement: FormControl<string | null>;
};

@Component({
  selector: 'app-personal-data',
  templateUrl: './personal-data.component.html',
  styleUrls: ['./personal-data.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class PersonalDataComponent extends BeneficiaryBaseComponent implements OnInit, OnDestroy {
  @ViewChild('instance', { static: true }) instance: NgbTypeahead;
  public personalDataForm: FormGroup<{
    title: FormControl<Title>;
    usualName: FormControl<string>;
    surname: FormControl<string>;
    firstName: FormControl<string>;
    secondName: FormControl<string>;
    birthDate: FormControl<Date>;
    lastUpdateDate: FormControl<Date>;
    nationality: FormControl<string>;
    birthPostalCode: FormControl<string>;
    birthCity: FormControl<string>;
    jobCenterNumber: FormControl<string>;
    attachedOrganization: FormControl<string>;
    recipientNumber: FormControl<string>;
    landlineNumber: FormControl<string>;

    cellPhoneNumber: FormControl<string>;
    sms: FormControl<boolean>;
    email: FormControl<boolean>;
    emailAddress: FormControl<string>;
    supportForm: FormGroup<{
      beginDateSupport: FormControl<Date>;
      supportTypeId: FormControl<number>;
      typeSupportDetail: FormControl<string>;
      status: FormControl<string>;
    }>;
    familialDataForm: FormGroup<{
      maritalStatus: FormControl<string>;
      childrenCount: FormControl<number>;
      dependentsNumber: FormControl<number>;
      singleParent: FormControl<boolean>;
      healthInsuranceId: FormControl<number>;
    }>;
    addressForm: FormGroup<addressFormType>;
    identicalPostalAddress: FormControl<boolean | null>;
    postalAddressForm: FormGroup<postalAddressForm>;
    iodasNumber: FormControl<string>;
    beginRsaDate: FormControl<Date>;
    orientedBy: FormControl<string | null>;
    followedBy: FormControl<string | null>;
    supportStatus: FormControl<boolean | null>;
    supportStatusTypeIds: FormControl<number[] | null>;
    isEmployee: FormControl<boolean | null>;
    isJobSeeker: FormControl<boolean | null>;
    isIndependent: FormControl<boolean | null>;
    isStudent: FormControl<boolean | null>;
    inTraining: FormControl<boolean | null>;
    isOther: FormControl<boolean | null>;
  }>;
  public submitted: boolean = false;
  public residentialAddressHistorized: boolean = false;
  public postalAddressHistorized: boolean = false;
  public residentialAddressHistoryInvalid: boolean = false;
  public postalAddressHistoryInvalid: boolean = false;

  public readonly attachedOrganizations: Map<AttachedOrganization, string> = labelsByOrganization;
  public readonly nationalities: Map<Nationality, string> = labelsByNationality;
  public stringAscOrder: (a: KeyValue<string, string>, b: KeyValue<string, string>) => number = StringUtils.stringAscOrder;
  public readonly residentialStatusLabel = ResidentialStatusLabel;
  public readonly residentialStatusMap: KeyValue<ResidentialStatus, string>[] = labelsByResidentialStatus;
  public readonly domiciliationTypeLabel = DomiciliationTypeLabel;
  public readonly domiciliationTypeMap: KeyValue<DomiciliationType, string>[] = labelsByDomiciliationType;
  public readonly orientedByList: Map<OrientedBy, string> = labelsByOrientedBy;
  public readonly StringUtils: StringUtils = StringUtils;
  public readonly maritalStatusMap: Map<MaritalStatus, string> = labelsByMaritalStatus;
  public readonly folderStatusMap: Map<FolderStatus, string> = labelsByFolderStatus;

  public searching: boolean = false;
  public searchFailed: boolean = false;
  public enabled: boolean = false;

  public clis: DisplayLabel[] = [];
  public ctms: DisplayLabel[] = [];
  public qpvs: DisplayLabel[] = [];
  public supportTypes: DisplayLabel[] = [];
  public supportStatusTypes: DisplayLabel[] = [{ id: 1, label: 'label' }];
  public healthInsurances: DisplayLabel[] = [];

  protected user: UserDto | undefined = undefined;
  private readonly defaultTown: TownDto = { denomination: 'Ville étrangère', postalCode: '99', cliId: 99, ctmId: 99, isMetropole: false };
  public differentStructure: boolean = false;

  @ViewChildren(SortableDirective) headers: QueryList<SortableDirective>;

  public residentialAddressHistories: BeneficiaryAddressDto[] = [];
  public postalAddressHistories: BeneficiaryAddressDto[] = [];

  constructor(
    private readonly supportTypeService: SupportTypeService,
    private readonly beneficiaryService: BeneficiariesService,
    private readonly toastService: ToastService,
    private readonly modalService: NgbModal,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly townService: TownService,
    private readonly areaService: AreaService,
    private readonly qpvService: QPVService,
    private readonly supportStatusTypeService: SupportStatusTypeService,
    private readonly dateService: DateService,
    private readonly healthInsurancesService: HealthInsurancesService,
    private readonly beneficiaryComplementsService: BeneficiaryComplementsService,
  ) {
    super();
  }

  public async ngOnInit(): Promise<void> {
    this.user = await this.authService.getUser();
    [this.clis, this.ctms] = await this.areaService.findAllClisAndCtms(true);
    this.beneficiaryService.beneficiary.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((beneficiary: BeneficiaryDto) => (this.beneficiary = beneficiary));
    const isReception: boolean = this.user.groups.includes('RECEPTION');
    const userIsStructure: boolean = this.user.groups.includes('STRUCTURE'); // Nouvelle variable pour la clarté

    this.differentStructure =
      !this.isCreateMode() &&
      ['RECEPTION', 'STRUCTURE'].some((group) => this.user.groups.includes(group)) &&
      this.user.structureId !== this.beneficiary.orientation.structure.id;
    this.initForm(isReception, this.differentStructure);
    if (this.isCreateMode() && this.user?.groups?.includes('TERRITORY') && this.user?.cliIds?.length === 1) {
      this.addressForm.controls.cliId.setValue(this.user.cliIds[0]);
    }
    this.qpvs = await this.qpvService.findAll();

    this.supportTypes = (await this.supportTypeService.findAll()).filter((value) => value.id !== SupportTypes.HORS_ACCOMPAGNEMENT);

    if (userIsStructure) {
      const desiredIds = [SupportTypes.RSJ_ID, SupportTypes.ITE_ID_AUTRE, SupportTypes.SOCIO_PRO];

      this.supportTypes = this.supportTypes.filter((value) => desiredIds.includes(value.id));
    } else if (isReception) {
      this.supportStatusTypes = (await this.supportStatusTypeService.findAll())
        .map((value: DisplayLabel) => ({
          category: 'Tous',
          ...value,
        }))
        .sort((a, b) => (a.label < b.label ? -1 : 1));
    }

    if (this.isCreateMode()) {
      this.healthInsurances = await this.healthInsurancesService.findAll();

      this.supportForm.controls.beginDateSupport.setValidators([Validators.required, DateValidators.dateFormatValidator]);
      this.supportForm.controls.beginDateSupport.updateValueAndValidity();
      this.supportForm.controls.supportTypeId.setValidators([Validators.required]);
      this.supportForm.controls.supportTypeId.updateValueAndValidity();

      if (isReception) {
        this.personalDataForm.controls.identicalPostalAddress.setValue(true);
        this.supportForm.controls.supportTypeId.setValue(SupportTypes.RECEPTION);
        this.supportForm.controls.beginDateSupport.setValue(this.dateService.getCurrent());
        this.addressForm.controls.beginDate.setValue(this.dateService.getCurrent());
      }

      await this.openDuplicatesModal();
    } else if (!this.isCreateMode() && isReception) {
      const complements: BeneficiaryComplement = await this.beneficiaryComplementsService.findById(this.beneficiary.complementId);
      this.healthInsurances = await this.healthInsurancesService.findAll();

      this.familialDataForm.controls.childrenCount.setValue(complements.childrenCount);
      this.familialDataForm.controls.childrenCount.updateValueAndValidity();

      this.familialDataForm.controls.dependentsNumber.setValue(complements.dependentsNumber);
      this.familialDataForm.controls.dependentsNumber.updateValueAndValidity();

      this.familialDataForm.controls.singleParent.setValue(complements.singleParent);
      this.familialDataForm.controls.singleParent.updateValueAndValidity();

      this.familialDataForm.controls.healthInsuranceId.setValue(complements.healthInsuranceId);
      this.familialDataForm.controls.healthInsuranceId.updateValueAndValidity();
    } else {
      this.personalDataForm.controls.identicalPostalAddress.setValue(this.beneficiary.identicalPostalAddress);
    }

    // Single subscription, apply initial state, for identicalPostalAddress
    this.personalDataForm.controls.identicalPostalAddress.valueChanges
      .pipe(startWith(this.personalDataForm.controls.identicalPostalAddress.value), takeUntil(this.destroy$))
      .subscribe((value: boolean | null) => this.handleIdenticalPostalAddressValueChange(!!value));

    this.addressForm.valueChanges.subscribe(() => this.handleFormAddressValueChange());

    this.personalDataForm.controls.supportStatus.valueChanges.subscribe((value: boolean) => this.handleOrientedByChange(value));
    if (this.differentStructure) {
      this.addressForm.disable();
      this.postalAddressForm.disable();
    }
  }

  private initForm(isReception: boolean, differentStructure: boolean) {
    this.personalDataForm = new FormGroup({
      title: new FormControl<Title>({ value: this.beneficiary.title, disabled: differentStructure }, [Validators.required]),
      usualName: new FormControl<string>({ value: this.beneficiary.usualName, disabled: differentStructure }, [
        Validators.required,
        Validators.maxLength(32),
        Validators.pattern(Utils.alphabeticalPattern),
      ]),
      surname: new FormControl<string>({ value: this.beneficiary.surname, disabled: differentStructure }, [
        Validators.required,
        Validators.maxLength(32),
        Validators.pattern(Utils.alphabeticalPattern),
      ]),
      firstName: new FormControl<string>({ value: this.beneficiary.firstName, disabled: differentStructure }, [
        Validators.required,
        Validators.maxLength(32),
        Validators.pattern(Utils.alphabeticalPattern),
      ]),
      secondName: new FormControl<string>({ value: this.beneficiary.secondName, disabled: differentStructure }, [
        Validators.maxLength(32),
        Validators.pattern(Utils.alphabeticalPattern),
      ]),
      birthDate: new FormControl<Date>({ value: this.beneficiary.birthDate, disabled: differentStructure }, [
        Validators.required,
        DateValidators.dateFormatValidator,
      ]),
      lastUpdateDate: new FormControl<Date>(this.beneficiary.lastUpdateDate, [DateValidators.dateFormatValidator]),
      nationality: new FormControl<string>({ value: this.beneficiary.nationality, disabled: differentStructure }, [Validators.required]),
      birthPostalCode: new FormControl<string>({ value: this.beneficiary.birthPostalCode, disabled: differentStructure }, [
        Validators.maxLength(5),
        Validators.minLength(5),
        Validators.pattern('[0-9]*'),
      ]),
      birthCity: new FormControl<string>({ value: this.beneficiary.birthCity, disabled: differentStructure }, [
        Validators.pattern(Utils.alphabeticalPattern),
      ]),
      jobCenterNumber: new FormControl<string>(this.beneficiary.jobCenterNumber, [Validators.pattern('^(?=(?:.{8}|.{11})$)[0-9, A-Z,a-z]*$')]),
      attachedOrganization: new FormControl<string>(this.beneficiary.attachedOrganization),
      recipientNumber: new FormControl<string>(this.beneficiary.recipientNumber, [Validators.maxLength(20)]),
      landlineNumber: new FormControl<string>(this.beneficiary.landlineNumber, [
        Validators.maxLength(10),
        Validators.minLength(10),
        Validators.pattern('[0-9]*'),
      ]),
      cellPhoneNumber: new FormControl<string>(this.beneficiary.cellPhoneNumber, [
        Validators.maxLength(10),
        Validators.minLength(10),
        Validators.pattern('[0-9]*'),
      ]),
      sms: new FormControl<boolean>(this.beneficiary.sms),
      email: new FormControl<boolean>(this.beneficiary.email),
      emailAddress: new FormControl<string>(this.beneficiary.emailAddress, [Validators.email]),
      supportForm: new FormGroup({
        beginDateSupport: new FormControl<Date>(this.beneficiary.support?.beginDate),
        supportTypeId: new FormControl<number>(this.beneficiary.support?.supportType.id),
        typeSupportDetail: new FormControl<string>(this.beneficiary.support?.typeSupportDetail),
        status: new FormControl<string>(
          this.isCreateMode() ? labelsByFolderStatusKey(FolderStatus.InProgress) : null,
          this.isCreateMode() ? [Validators.required] : [],
        ),
      }),
      addressForm: new FormGroup(
        {
          id: new FormControl<number | null>(this.beneficiary.residentialAddress?.id),
          beginDate: new FormControl<Date | null>(this.beneficiary.residentialAddress?.beginDate, [
            Validators.required,
            DateValidators.dateFormatValidator,
          ]),
          endDate: new FormControl<Date | null>(this.beneficiary.residentialAddress?.endDate, [DateValidators.dateFormatValidator]),
          address: new FormControl<string | null>(this.beneficiary.residentialAddress?.address, [Validators.required]),
          additionalAddress: new FormControl<string | null>(this.beneficiary.residentialAddress?.additionalAddress),
          postalCode: new FormControl<string | null>(this.beneficiary.residentialAddress?.postalCode, [
            Validators.required,
            Validators.maxLength(5),
            Validators.minLength(5),
            Validators.pattern('[0-9]*'),
          ]),
          city: new FormControl<string | null>(this.beneficiary.residentialAddress?.city, [
            Validators.required,
            Validators.pattern(Utils.alphabeticalPattern),
          ]),
          qpvId: new FormControl<number | null>(this.beneficiary.residentialAddress?.qpvId ?? -1, [Validators.required]),
          cliId: new FormControl<number | null>(this.beneficiary.residentialAddress?.cliId),
          ctmLabel: new FormControl<string | null>(this.ctms.find((ctm) => ctm.id === this.beneficiary.residentialAddress?.ctmId)?.label),
          residentialStatus: new FormControl<string | null>(this.beneficiary.residentialAddress?.residentialStatus),
          residentialStatusComplement: new FormControl<string | null>(this.beneficiary.residentialAddress?.residentialStatusComplement),
        },
        {
          validators: [DateValidators.datesOrderValidator('beginDate', 'endDate')],
        },
      ),
      identicalPostalAddress: new FormControl<boolean | null>(
        { value: isReception ? true : this.beneficiary.identicalPostalAddress, disabled: differentStructure },
        [Validators.required],
      ),
      postalAddressForm: new FormGroup(
        {
          id: new FormControl<number | null>(this.beneficiary.postalAddress?.id),
          postalBeginDate: new FormControl<Date | null>(this.beneficiary.postalAddress?.beginDate, [DateValidators.dateFormatValidator]),
          postalEndDate: new FormControl<Date | null>(this.beneficiary.postalAddress?.endDate, [DateValidators.dateFormatValidator]),
          postalAddress: new FormControl<string | null>(this.beneficiary.postalAddress?.address),
          postalAdditionalAddress: new FormControl<string | null>(this.beneficiary.postalAddress?.additionalAddress),
          postalPostalCode: new FormControl<string | null>(this.beneficiary.postalAddress?.postalCode, [
            Validators.maxLength(5),
            Validators.minLength(5),
            Validators.pattern('[0-9]*'),
          ]),
          postalCity: new FormControl<string | null>(this.beneficiary.postalAddress?.city, [Validators.pattern(Utils.alphabeticalPattern)]),
          domiciliationType: new FormControl<string | null>(this.beneficiary.postalAddress?.domiciliationType),
          domiciliationTypeComplement: new FormControl<string | null>(this.beneficiary.postalAddress?.domiciliationTypeComplement),
        },
        {
          validators: [DateValidators.datesOrderValidator('postalBeginDate', 'postalEndDate')],
        },
      ),
      iodasNumber: new FormControl<string | null>(this.beneficiary?.iodasNumber),
      beginRsaDate: new FormControl<Date | null>(undefined, [DateValidators.dateFormatValidator]),
      orientedBy: new FormControl<string | null>(this.beneficiary?.orientedBy, [isReception ? Validators.required : Validators.nullValidator]),
      followedBy: new FormControl<string | null>(this.beneficiary.followedBy ? this.beneficiary.followedBy : undefined, [Validators.maxLength(30)]),
      supportStatus: new FormControl<boolean | null>(this.beneficiary?.supportStatus, [isReception ? Validators.required : Validators.nullValidator]),
      supportStatusTypeIds: new FormControl<number[] | null>(
        { value: this.beneficiary?.supportStatusTypeIds, disabled: !this.beneficiary?.supportStatus },
        [isReception ? Validators.required : Validators.nullValidator],
      ),
      isEmployee: new FormControl<boolean | null>(this.beneficiary?.isEmployee),
      isJobSeeker: new FormControl<boolean | null>(this.beneficiary?.isJobSeeker),
      isIndependent: new FormControl<boolean | null>(this.beneficiary?.isIndependent),
      isStudent: new FormControl<boolean | null>(this.beneficiary?.isStudent),
      inTraining: new FormControl<boolean | null>(this.beneficiary?.inTraining),
      isOther: new FormControl<boolean | null>(this.beneficiary?.isOther),
      familialDataForm: new FormGroup({
        maritalStatus: new FormControl<string>(this.beneficiary.maritalStatus, this.isCreateMode() ? [Validators.required] : []),
        childrenCount: new FormControl<number>(null, [Validators.pattern('[0-9]*')]),
        dependentsNumber: new FormControl<number>(null, [Validators.pattern('[0-9]*')]),
        singleParent: new FormControl<boolean>(null, this.isCreateMode() ? [Validators.required] : []),
        healthInsuranceId: new FormControl<number>(null),
      }),
    });
    this.residentialAddressHistories = this.beneficiary.residentialAddresses
      ? [...this.beneficiary.residentialAddresses]
          .filter((address) => address.historized)
          .sort((a: BeneficiaryAddressDto, b: BeneficiaryAddressDto) => (a.beginDate < b.beginDate ? 1 : -1))
      : [];
    this.postalAddressHistories = this.beneficiary.postalAddresses
      ? [...this.beneficiary.postalAddresses.filter((address) => address.historized)].sort((a: BeneficiaryAddressDto, b: BeneficiaryAddressDto) =>
          a.beginDate < b.beginDate ? 1 : -1,
        )
      : [];
    if (isReception) {
      this.personalDataForm.addValidators(() => {
        if (
          !this.personalDataForm.controls.isEmployee.value &&
          !this.personalDataForm.controls.isJobSeeker.value &&
          !this.personalDataForm.controls.isIndependent.value &&
          !this.personalDataForm.controls.isStudent.value &&
          !this.personalDataForm.controls.inTraining.value &&
          !this.personalDataForm.controls.isOther.value
        ) {
          return { situationRequired: true };
        }
        return null;
      });
    }
  }

  private handleOrientedByChange(value: boolean) {
    if (value) {
      this.personalDataForm.controls.supportStatusTypeIds.enable();
    } else {
      this.personalDataForm.controls.supportStatusTypeIds.reset();
      this.personalDataForm.controls.supportStatusTypeIds.disable();
    }
  }

  public async onSubmit(): Promise<void> {
    this.submitted = true;
    this.resetAddressValidator('residential');
    this.resetAddressValidator('postal');
    this.addressHistoryValid('residential');
    if (!this.personalDataForm.controls.identicalPostalAddress.value) {
      this.addressHistoryValid('postal');
    }
    if (this.personalDataForm.valid && !this.postalAddressHistoryInvalid && !this.residentialAddressHistoryInvalid) {
      if (
        !this.isCreateMode() &&
        (this.personalDataForm.controls.usualName.dirty ||
          this.personalDataForm.controls.firstName.dirty ||
          this.personalDataForm.controls.birthDate.dirty) &&
        !(await this.checkForDuplicates())
      ) {
        return;
      }
      this.normalizeNames();
      this.markNamesAndBirthDateAsPristine();

      const residentialAddress: BeneficiaryAddressDto = {
        id: this.addressForm.controls.id.value,
        address: this.addressForm.controls.address.value,
        beginDate: this.addressForm.controls.beginDate.value,
        endDate: this.addressForm.controls.endDate.value,
        additionalAddress: this.addressForm.controls.additionalAddress.value,
        postalCode: this.addressForm.controls.postalCode.value,
        city: this.addressForm.controls.city.value,
        cliId: this.addressForm.controls.cliId.value,
        ctmId: this.ctms.find((ctm) => ctm.label === this.addressForm.controls.ctmLabel.value)?.id,
        qpvId: this.addressForm.controls.qpvId.value === -1 ? null : this.addressForm.controls.qpvId.value,
        residentialStatus: this.addressForm.controls.residentialStatus.value,
        residentialStatusComplement: this.addressForm.controls.residentialStatusComplement.value,
        beneficiaryId: this.beneficiary.id,
        historized: false,
        addressType: AddressType.residence,
      };
      const postalAddress: BeneficiaryAddressDto = {
        id: this.postalAddressForm.controls.id.value,
        address: this.postalAddressForm.controls.postalAddress.value,
        beginDate: this.postalAddressForm.controls.postalBeginDate.value,
        endDate: this.postalAddressForm.controls.postalEndDate.value,
        additionalAddress: this.postalAddressForm.controls.postalAdditionalAddress.value,
        postalCode: this.postalAddressForm.controls.postalPostalCode.value,
        city: this.postalAddressForm.controls.postalCity.value,
        domiciliationType: this.postalAddressForm.controls.domiciliationType.value,
        domiciliationTypeComplement: this.postalAddressForm.controls.domiciliationTypeComplement.value,
        beneficiaryId: this.beneficiary.id,
        historized: false,
        addressType: AddressType.postal,
      };

      let beneficiaryUpdated: BeneficiaryDto = {
        id: this.beneficiary?.id,
        title: this.personalDataForm.controls.title.value,
        iodasNumber: this.personalDataForm.controls.iodasNumber.value,
        usualName: this.personalDataForm.controls.usualName.value,
        surname: this.personalDataForm.controls.surname.value,
        firstName: this.personalDataForm.controls.firstName.value,
        secondName: this.personalDataForm.controls.secondName.value,
        birthDate: this.personalDataForm.controls.birthDate.value,
        nationality: this.personalDataForm.controls.nationality.value,
        birthPostalCode: this.personalDataForm.controls.birthPostalCode.value,
        birthCity: this.personalDataForm.controls.birthCity.value,
        jobCenterNumber: this.personalDataForm.controls.jobCenterNumber.value,
        attachedOrganization: this.personalDataForm.controls.attachedOrganization.value,
        recipientNumber: this.personalDataForm.controls.recipientNumber.value,
        landlineNumber: this.personalDataForm.controls.landlineNumber.value,
        cellPhoneNumber: this.personalDataForm.controls.cellPhoneNumber.value,
        sms: this.personalDataForm.controls.sms.value,
        email: this.personalDataForm.controls.email.value,
        emailAddress: this.personalDataForm.controls.emailAddress.value,
        lastUpdateDate: this.personalDataForm.controls.lastUpdateDate.value,
        residentialAddress,
        postalAddress,
        identicalPostalAddress: this.personalDataForm.controls.identicalPostalAddress.value,
        addresses: [residentialAddress, postalAddress, ...this.residentialAddressHistories, ...this.postalAddressHistories],
        supportStatus: this.personalDataForm.controls.supportStatus.value,
        supportStatusTypeIds: this.personalDataForm.controls.supportStatusTypeIds.value,
        isEmployee: this.personalDataForm.controls.isEmployee.value,
        isJobSeeker: this.personalDataForm.controls.isJobSeeker.value,
        isIndependent: this.personalDataForm.controls.isIndependent.value,
        isOther: this.personalDataForm.controls.isOther.value,
        isStudent: this.personalDataForm.controls.isStudent.value,
        inTraining: this.personalDataForm.controls.inTraining.value,
        orientedBy: this.personalDataForm.controls.orientedBy.value,
        followedBy: this.personalDataForm.controls.followedBy.value,
        referents: this.beneficiary.referents,
        intensities: this.beneficiary.intensities,
        orientations: this.beneficiary.orientations,
        supports: this.beneficiary.supports,
      } as BeneficiaryDto;

      if (this.isCreateMode()) {
        beneficiaryUpdated.support = {
          beginDate: this.supportForm.controls.beginDateSupport.value,
          typeSupportDetail: this.supportForm.controls.typeSupportDetail.value,
          supportType: { id: this.supportForm.controls.supportTypeId.value } as SupportTypeDto,
        } as SupportDto;
        beneficiaryUpdated.beginRsaDate = this.personalDataForm.controls.beginRsaDate.value;

        const complement: BeneficiaryComplementSaveDto = {
          maritalStatus: this.familialDataForm.controls.maritalStatus.value,
          childrenCount: this.familialDataForm.controls.childrenCount.value,
          dependentsNumber: this.familialDataForm.controls.dependentsNumber.value,
          healthInsuranceId: this.familialDataForm.controls.healthInsuranceId.value,
          singleParent: this.familialDataForm.controls.singleParent.value,
          status: this.supportForm.controls.status.value,
        } as BeneficiaryComplementSaveDto;

        await this.createBeneficiary(beneficiaryUpdated, complement);
      } else {
        await this.beneficiaryService.updateBeneficiary(beneficiaryUpdated);
        if (this.user.groups.includes('RECEPTION') && (this.familialDataForm.touched || this.supportForm.touched)) {
          const complement: BeneficiaryComplementSaveDto = {
            maritalStatus: this.familialDataForm.controls.maritalStatus.value,
            childrenCount: this.familialDataForm.controls.childrenCount.value,
            dependentsNumber: this.familialDataForm.controls.dependentsNumber.value,
            healthInsuranceId: this.familialDataForm.controls.healthInsuranceId.value,
            singleParent: this.familialDataForm.controls.singleParent.value,
            status: this.supportForm.controls.status.value,
          } as BeneficiaryComplementSaveDto;
          await this.beneficiaryComplementsService.updateComplementBeneficiary(this.beneficiary.id, { beneficiaryComplement: complement });
        }

        if (this.user.groups.includes('RECEPTION') && this.familialDataForm.touched) {
          const complement: BeneficiaryComplementSaveDto = {
            maritalStatus: this.familialDataForm.controls.maritalStatus.value,
            childrenCount: this.familialDataForm.controls.childrenCount.value,
            dependentsNumber: this.familialDataForm.controls.dependentsNumber.value,
            healthInsuranceId: this.familialDataForm.controls.healthInsuranceId.value,
            singleParent: this.familialDataForm.controls.singleParent.value,
            status: this.supportForm.controls.status.value,
          } as BeneficiaryComplementSaveDto;
          await this.beneficiaryComplementsService.updateComplementBeneficiary(this.beneficiary.id, { beneficiaryComplement: complement });
        }
        this.toastService.show("L'état civil a été sauvegardé avec succès.", 'success');
      }
    } else {
      this.toastService.show('La sauvegarde a échoué, veuillez vérifier les données.', 'danger');
    }
  }

  public async cancel(): Promise<void> {
    if (!this.isCreateMode()) {
      this.personalDataForm.patchValue(this.beneficiary, { emitEvent: true });
      this.addressForm.patchValue(this.beneficiary.residentialAddress, { emitEvent: true });
      this.patchPostalAddressValue();
      this.submitted = false;
      this.residentialAddressHistorized = false;
      this.postalAddressHistorized = false;
      this.residentialAddressHistoryInvalid = false;
      this.postalAddressHistoryInvalid = false;
      this.residentialAddressHistories = this.beneficiary.residentialAddresses.filter((address) => address.historized);
      this.postalAddressHistories = this.beneficiary.postalAddresses.filter((address) => address.historized);
      this.resetAddressValidator('residential');
      this.resetAddressValidator('postal');
      this.markNamesAndBirthDateAsPristine();
    } else {
      this.router.navigate(['/beneficiaries']);
    }
  }

  private patchPostalAddressValue(): void {
    this.postalAddressForm.controls.postalBeginDate.setValue(this.beneficiary.postalAddress?.beginDate);
    this.postalAddressForm.controls.postalEndDate.setValue(this.beneficiary.postalAddress?.endDate);
    this.postalAddressForm.controls.domiciliationType.setValue(this.beneficiary.postalAddress?.domiciliationType);
    this.postalAddressForm.controls.domiciliationTypeComplement.setValue(this.beneficiary.postalAddress?.domiciliationTypeComplement);
    this.postalAddressForm.controls.postalAddress.setValue(this.beneficiary.postalAddress?.address);
    this.postalAddressForm.controls.postalAdditionalAddress.setValue(this.beneficiary.postalAddress?.additionalAddress);
    this.postalAddressForm.controls.postalCity.setValue(this.beneficiary.postalAddress?.city);
    this.postalAddressForm.controls.postalPostalCode.setValue(this.beneficiary.postalAddress?.postalCode);
    this.postalAddressForm.updateValueAndValidity();
  }

  public async deleteAddressHistory(addressHistory: BeneficiaryAddressDto, addressType: string): Promise<void> {
    if (this.user && this.user.groups && this.user.groups.includes('ADMIN')) {
      const confirm: boolean = await this.openDeleteConfirmationModal();
      if (confirm) {
        if (addressType === 'residential') {
          this.residentialAddressHistories.splice(
            this.residentialAddressHistories.findIndex((item: BeneficiaryAddressDto) => {
              return item.id === addressHistory.id;
            }),
            1,
          );
        } else if (addressType === 'postal') {
          this.postalAddressHistories.splice(
            this.postalAddressHistories.findIndex((item: BeneficiaryAddressDto) => {
              return item.id === addressHistory.id;
            }),
            1,
          );
        }
      }
    }
  }

  public historizeAddress(addressType: string): void {
    if (
      !this.isCreateMode() &&
      this.user &&
      this.user.groups &&
      (this.user.groups.includes('ADMIN') ||
        this.user.groups.includes('TERRITORY') ||
        this.user.groups.includes('STRUCTURE') ||
        this.user.groups.includes('DIRECTION+'))
    ) {
      this.residentialAddressHistorized = addressType === 'residential';
      this.postalAddressHistorized = addressType === 'postal';
      this.setAddressValidator(addressType);
      if (addressType === 'residential' && this.personalDataForm.controls.addressForm.valid) {
        this.historyAddress(addressType);
        if (this.personalDataForm.controls.identicalPostalAddress.value) {
          this.historyAddress('postal');
          this.resetAddress('postal');
        }
        this.resetAddress(addressType);
        this.resetAddressValidator(addressType);
      } else if (addressType === 'postal' && this.personalDataForm.controls.postalAddressForm.valid) {
        this.historyAddress(addressType);
        this.resetAddress(addressType);
        this.resetAddressValidator(addressType);
      }
    } else {
      this.toastService.show("Vous n'avez pas l'autorisation d'historiser l'adresse du bénéficiaire.", 'danger');
    }
  }

  private setAddressValidator(addressType: string) {
    if (addressType === 'residential') {
      this.addressForm.controls.endDate.setValidators([Validators.required, DateValidators.dateFormatValidator]);
      this.addressForm.controls.endDate.updateValueAndValidity();
    } else {
      this.postalAddressForm.controls.postalEndDate.setValidators([Validators.required, DateValidators.dateFormatValidator]);
      this.postalAddressForm.controls.postalEndDate.updateValueAndValidity();
    }
  }

  public resetAddressValidator(addressType: string): void {
    if (addressType === 'residential') {
      this.residentialAddressHistorized = false;
      this.addressForm.controls.endDate.clearValidators();
      this.addressForm.controls.endDate.setValidators([DateValidators.dateFormatValidator]);
      this.addressForm.controls.endDate.updateValueAndValidity();
    } else {
      this.postalAddressHistorized = false;
      this.postalAddressForm.controls.postalEndDate.clearValidators();
      this.postalAddressForm.controls.postalEndDate.setValidators([DateValidators.dateFormatValidator]);
      this.postalAddressForm.controls.postalEndDate.updateValueAndValidity();
    }
  }

  public get addressForm(): FormGroup {
    return this.personalDataForm.controls.addressForm as FormGroup;
  }

  public get postalAddressForm(): FormGroup {
    return this.personalDataForm.controls.postalAddressForm as FormGroup;
  }

  public get supportForm(): FormGroup {
    return this.personalDataForm.controls.supportForm as FormGroup;
  }

  public get familialDataForm(): FormGroup {
    return this.personalDataForm.controls.familialDataForm as FormGroup;
  }

  public isCreateMode(): boolean {
    return this.beneficiary.id === undefined;
  }

  public onFocus(event: any): void {
    event.target.dispatchEvent(new Event('input'));
  }

  public searchBirthCity: (text$: Observable<string>) => Observable<TownDto[]> = (text$: Observable<string>): Observable<TownDto[]> =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      tap(() => (this.searching = true)),
      switchMap((text: string) =>
        text.length < 1 ? of([this.defaultTown]) : this.townService.findByText(text).pipe(tap(() => (this.searchFailed = false))),
      ),
      tap(() => (this.searching = false)),
    );

  public search: (text$: Observable<string>) => Observable<TownDto[]> = (text$: Observable<string>): Observable<TownDto[]> =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      tap(() => (this.searching = true)),
      switchMap((text: string) => (text.length < 1 ? [] : this.townService.findByText(text).pipe(tap(() => (this.searchFailed = false))))),
      tap(() => (this.searching = false)),
    );

  public inputFormat: (value: TownDto | string) => string = (value: TownDto | string): string => (this.isTownDto(value) ? value.denomination : value);
  public resultFormat: (value: TownDto) => string = (value: TownDto): string => value.postalCode + ' - ' + value.denomination;

  public onSelectBirthCity(event: any, input: any): void {
    event.preventDefault();
    if (event && event.item) {
      this.personalDataForm.controls.birthPostalCode.setValue(event.item.postalCode === '99' ? '' : event.item.postalCode);
      this.personalDataForm.controls.birthCity.setValue(event.item.denomination);
      input.value = event.item.denomination;
    }
  }

  public onBlurBirthCity(input: any): void {
    if (input && input.value === '') {
      this.personalDataForm.controls.birthPostalCode.setValue('');
      this.personalDataForm.controls.birthCity.setValue('');
    }
  }

  public onSelectItem(event: any, input: any): void {
    event.preventDefault();
    if (event && event.item) {
      this.addressForm.controls.postalCode.setValue(event.item.postalCode);
      this.addressForm.controls.city.setValue(event.item.denomination);
      this.addressForm.controls.ctmLabel.setValue(this.ctms.find((ctm) => ctm.id === event.item.ctmId)?.label);
      this.addressForm.controls.cliId.setValue(event.item.cliId);
      input.value = event.item.denomination;
    }
  }

  public onBlur(input: any): void {
    if (input && input.value === '') {
      this.addressForm.controls.postalCode.setValue('');
      this.addressForm.controls.city.setValue('');
      this.addressForm.controls.ctmLabel.setValue('');
    }
  }

  public onBlurPostalCity(input: any): void {
    if (input && input.value === '') {
      this.postalAddressForm.controls.postalPostalCode.setValue('');
      this.postalAddressForm.controls.postalCity.setValue('');
    }
  }

  public onSelectPostalCity(event: any, input: any): void {
    event.preventDefault();
    if (event && event.item) {
      this.postalAddressForm.controls.postalPostalCode.setValue(event.item.postalCode);
      this.postalAddressForm.controls.postalCity.setValue(event.item.denomination);
      input.value = event.item.denomination;
    }
  }

  public searchPostalCity: (text$: Observable<string>) => Observable<TownDto[]> = (text$: Observable<string>): Observable<TownDto[]> =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      tap(() => (this.searching = true)),
      switchMap((text: string) => (text.length < 1 ? [] : this.townService.findByText(text).pipe(tap(() => (this.searchFailed = false))))),
      tap(() => (this.searching = false)),
    );

  private isTownDto(value: any): value is TownDto {
    return value instanceof TownDto;
  }

  private historyAddress(addressType: string): void {
    let newAddressHistory: BeneficiaryAddressDto;
    if (addressType === 'residential') {
      newAddressHistory = {
        id: this.addressForm.controls.id.value,
        beginDate: this.addressForm.controls.beginDate.value,
        endDate: this.addressForm.controls.endDate.value,
        address: this.addressForm.controls.address.value,
        additionalAddress: this.addressForm.controls.additionalAddress.value,
        postalCode: this.addressForm.controls.postalCode.value,
        city: this.addressForm.controls.city.value,
        cliLabel: this.clis.find((cli) => cli.id === this.addressForm.controls.cliId.value)?.label,
        ctmLabel: this.addressForm.controls.ctmLabel.value,
        qpvLabel: this.qpvs.find((qpv) => qpv.id === this.addressForm.controls.qpvId.value)?.label,
        cliId: this.addressForm.controls.cliId.value,
        ctmId: this.ctms.find((ctm) => ctm.label === this.addressForm.controls.ctmLabel.value)?.id,
        qpvId: this.addressForm.controls.qpvId.value === -1 ? null : this.addressForm.controls.qpvId.value,
        residentialStatus: this.addressForm.controls.residentialStatus.value,
        residentialStatusComplement: this.addressForm.controls.residentialStatusComplement.value,
        beneficiaryId: this.beneficiary.id,
        historized: true,
        addressType: AddressType.residence,
      } as BeneficiaryAddressDto;
    } else if (addressType === 'postal') {
      newAddressHistory = {
        id: this.postalAddressForm.controls.id.value,
        beginDate: this.postalAddressForm.controls.postalBeginDate.value,
        endDate: this.postalAddressForm.controls.postalEndDate.value,
        address: this.postalAddressForm.controls.postalAddress.value,
        additionalAddress: this.postalAddressForm.controls.postalAdditionalAddress.value,
        postalCode: this.postalAddressForm.controls.postalPostalCode.value,
        city: this.postalAddressForm.controls.postalCity.value,
        domiciliationType: this.postalAddressForm.controls.domiciliationType.value,
        domiciliationTypeComplement: this.postalAddressForm.controls.domiciliationTypeComplement.value,
        beneficiaryId: this.beneficiary.id,
        historized: true,
        addressType: AddressType.postal,
      } as BeneficiaryAddressDto;
    }

    if (addressType === 'residential') {
      if (this.residentialAddressHistories) {
        this.residentialAddressHistories.push(newAddressHistory);
      } else {
        this.residentialAddressHistories = [newAddressHistory];
      }
      this.residentialAddressHistories.sort((a: BeneficiaryAddressDto, b: BeneficiaryAddressDto) => (a.beginDate < b.beginDate ? 1 : -1));
    } else if (addressType === 'postal') {
      if (this.postalAddressHistories) {
        this.postalAddressHistories.push(newAddressHistory);
      } else {
        this.postalAddressHistories = [newAddressHistory];
      }
      this.postalAddressHistories.sort((a: BeneficiaryAddressDto, b: BeneficiaryAddressDto) => (a.beginDate < b.beginDate ? 1 : -1));
    }
  }

  private resetAddress(addressType: string): void {
    if (addressType === 'residential') {
      this.addressForm.controls.id.reset();
      this.addressForm.controls.beginDate.reset();
      this.addressForm.controls.endDate.reset();
      this.addressForm.controls.address.reset();
      this.addressForm.controls.additionalAddress.reset();
      this.addressForm.controls.postalCode.reset();
      this.addressForm.controls.city.reset();
      this.addressForm.controls.cliId.reset();
      this.addressForm.controls.ctmLabel.reset();
      this.addressForm.controls.qpvId.reset();
      this.addressForm.controls.residentialStatus.reset();
      this.addressForm.controls.residentialStatusComplement.reset();
    } else {
      this.postalAddressForm.controls.id.reset();
      this.postalAddressForm.controls.postalBeginDate.reset();
      this.postalAddressForm.controls.postalEndDate.reset();
      this.postalAddressForm.controls.postalAddress.reset();
      this.postalAddressForm.controls.postalAdditionalAddress.reset();
      this.postalAddressForm.controls.postalPostalCode.reset();
      this.postalAddressForm.controls.postalCity.reset();
      this.postalAddressForm.controls.domiciliationType.reset();
      this.postalAddressForm.controls.domiciliationTypeComplement.reset();
    }
  }

  private async openDeleteConfirmationModal(): Promise<boolean> {
    const modal: NgbModalRef = this.modalService.open(ConfirmationComponent, {
      backdrop: 'static',
      centered: true,
    });
    const modalComponent: ConfirmationComponent = modal.componentInstance as ConfirmationComponent;
    return modalComponent.confirm("Êtes-vous sûr(e) de vouloir supprimer cette historisation d'adresse ?");
  }

  private addressHistoryValid(addressType: string): void {
    let addressHistoryList: BeneficiaryAddressDto[];
    if (addressType === 'residential') {
      this.residentialAddressHistoryInvalid = false;
      addressHistoryList = this.residentialAddressHistories;
    } else if (addressType === 'postal') {
      this.postalAddressHistoryInvalid = false;
      addressHistoryList = this.postalAddressHistories;
    }
    if (addressHistoryList) {
      for (let i: number = 0; i < addressHistoryList.length; i++) {
        if (
          !addressHistoryList[i].beginDate ||
          !addressHistoryList[i].endDate ||
          moment(addressHistoryList[i].beginDate).isSameOrAfter(addressHistoryList[i].endDate, 'd') ||
          (i + 1 < addressHistoryList.length && moment(addressHistoryList[i].beginDate).isSameOrBefore(addressHistoryList[i + 1].endDate, 'd'))
        ) {
          if (addressType === 'residential') {
            this.residentialAddressHistoryInvalid = true;
          } else if (addressType === 'postal') {
            this.postalAddressHistoryInvalid = true;
          }
        }
      }
      if (addressHistoryList[0]) {
        if (addressType === 'residential' && moment(this.addressForm.controls.beginDate.value).isSameOrBefore(addressHistoryList[0].endDate, 'd')) {
          this.residentialAddressHistoryInvalid = true;
        } else if (
          moment(addressType === 'postal' && this.postalAddressForm.controls.postalBeginDate.value).isSameOrBefore(addressHistoryList[0].endDate, 'd')
        ) {
          this.postalAddressHistoryInvalid = true;
        }
      }
    }
  }

  public enableTable(): void {
    if (this.enabled) {
      this.enabled = false;
    } else {
      this.enabled = true;
    }
  }

  private handleIdenticalPostalAddressValueChange(value: boolean) {
    if (value === true) {
      this.postalAddressForm.controls.postalBeginDate.disable();
      this.postalAddressForm.controls.postalBeginDate.setValue(this.addressForm.controls.beginDate.value);
      this.postalAddressForm.controls.postalEndDate.disable();
      this.postalAddressForm.controls.postalEndDate.setValue(this.addressForm.controls.endDate.value);
      this.postalAddressForm.controls.domiciliationType.disable();
      this.postalAddressForm.controls.domiciliationType.setValue(null);
      this.postalAddressForm.controls.domiciliationTypeComplement.disable();
      this.postalAddressForm.controls.domiciliationTypeComplement.setValue(null);
      this.postalAddressForm.controls.postalAddress.disable();
      this.postalAddressForm.controls.postalAddress.setValue(this.addressForm.controls.address.value);
      this.postalAddressForm.controls.postalAdditionalAddress.disable();
      this.postalAddressForm.controls.postalAdditionalAddress.setValue(this.addressForm.controls.additionalAddress.value);
      this.postalAddressForm.controls.postalCity.disable();
      this.postalAddressForm.controls.postalCity.setValue(this.addressForm.controls.city.value);
      this.postalAddressForm.controls.postalPostalCode.setValue(this.addressForm.controls.postalCode.value);
      this.postalAddressForm.controls.postalCity.setValidators([Validators.pattern(Utils.alphabeticalPattern)]);
      this.postalAddressForm.controls.postalPostalCode.setValidators([
        Validators.maxLength(5),
        Validators.minLength(5),
        Validators.pattern('[0-9]*'),
      ]);
      this.postalAddressForm.controls.domiciliationType.setValidators(null);
      this.postalAddressForm.controls.postalAddress.setValidators(null);
      this.postalAddressForm.controls.postalBeginDate.setValidators([DateValidators.dateFormatValidator]);
    } else {
      this.postalAddressForm.controls.postalBeginDate.enable();
      this.postalAddressForm.controls.postalEndDate.enable();
      this.postalAddressForm.controls.domiciliationType.enable();
      this.postalAddressForm.controls.domiciliationTypeComplement.enable();
      this.postalAddressForm.controls.postalAddress.enable();
      this.postalAddressForm.controls.postalAdditionalAddress.enable();
      this.postalAddressForm.controls.postalCity.enable();
      this.postalAddressForm.controls.postalCity.setValidators([Validators.required, Validators.pattern(Utils.alphabeticalPattern)]);
      this.postalAddressForm.controls.postalPostalCode.setValidators([
        Validators.required,
        Validators.maxLength(5),
        Validators.minLength(5),
        Validators.pattern('[0-9]*'),
      ]);
      this.postalAddressForm.controls.domiciliationType.setValidators([Validators.required]);
      this.postalAddressForm.controls.postalAddress.setValidators([Validators.required]);
      this.postalAddressForm.controls.postalBeginDate.setValidators([Validators.required, DateValidators.dateFormatValidator]);
    }
    this.postalAddressForm.controls.postalCity.updateValueAndValidity();
    this.postalAddressForm.controls.postalPostalCode.updateValueAndValidity();
    this.postalAddressForm.controls.domiciliationType.updateValueAndValidity();
    this.postalAddressForm.controls.postalAddress.updateValueAndValidity();
    this.postalAddressForm.controls.postalBeginDate.updateValueAndValidity();
  }

  private handleFormAddressValueChange() {
    if (this.personalDataForm.controls.identicalPostalAddress.value) {
      this.postalAddressForm.controls.postalBeginDate.setValue(this.addressForm.controls.beginDate.value);
      this.postalAddressForm.controls.postalEndDate.setValue(this.addressForm.controls.endDate.value);
      this.postalAddressForm.controls.domiciliationType.setValue(null);
      this.postalAddressForm.controls.domiciliationTypeComplement.setValue(null);
      this.postalAddressForm.controls.postalAddress.setValue(this.addressForm.controls.address.value);
      this.postalAddressForm.controls.postalAdditionalAddress.setValue(this.addressForm.controls.additionalAddress.value);
      this.postalAddressForm.controls.postalCity.setValue(this.addressForm.controls.city.value);
      this.postalAddressForm.controls.postalPostalCode.setValue(this.addressForm.controls.postalCode.value);
    }
  }

  private async openDuplicatesModal(): Promise<void> {
    const modal: NgbModalRef = this.modalService.open(BeneficiaryDuplicateCheckComponent, {
      backdrop: 'static',
      centered: true,
      size: 'md',
    });
    const modalComponent: BeneficiaryDuplicateCheckComponent = modal.componentInstance as BeneficiaryDuplicateCheckComponent;
    const duplicateCheckDto: BeneficiaryDuplicateCheckDto = await modalComponent.confirm();
    this.personalDataForm.controls.firstName.setValue(duplicateCheckDto.firstName);
    this.personalDataForm.controls.firstName.disable();
    this.personalDataForm.controls.usualName.setValue(duplicateCheckDto.usualName);
    this.personalDataForm.controls.usualName.disable();
    this.personalDataForm.controls.birthDate.setValue(duplicateCheckDto.birthDate);
    this.personalDataForm.controls.birthDate.disable();
  }

  private async createBeneficiary(dto: BeneficiaryDto, complementDto: BeneficiaryComplementSaveDto): Promise<void> {
    try {
      dto = Utils.normalizeStrings(dto);
      let beneficiaryCreated: BeneficiaryDto;
      if ((this.user?.groups ?? []).includes('ADMIN')) {
        beneficiaryCreated = await this.beneficiaryService.createBeneficiaryAsAdmin(dto);
      } else {
        beneficiaryCreated = await this.beneficiaryService.createBeneficiary(dto);
      }

      this.beneficiaryComplementsService.updateComplementBeneficiary(beneficiaryCreated.id, { beneficiaryComplement: complementDto });

      if ((this.user?.groups ?? []).includes('RECEPTION')) {
        this.router.navigate(['/beneficiaries', beneficiaryCreated.id, 'folder', 'interviews']);
      } else {
        this.router.navigate(['/beneficiaries', beneficiaryCreated.id, 'details', 'complement']);
      }
      this.toastService.show('Le bénéficiaire a été créé avec succès.', 'success');
    } catch (e) {
      if (e.status === 460) {
        this.toastService.show("L'individu que vous voulez créer existe déjà.", 'warning');
      }
    }
  }

  private async checkForDuplicates(): Promise<boolean> {
    const dto: BeneficiaryDuplicateCheckDto = {
      firstName: this.personalDataForm.value.firstName,
      usualName: this.personalDataForm.value.usualName,
      birthDate: this.personalDataForm.value.birthDate,
      id: this.beneficiary?.id,
    } as BeneficiaryDuplicateCheckDto;
    try {
      await this.beneficiaryService.checkForDuplicate(dto);
      return true;
    } catch (error) {
      if (error.status === 460) {
        if ((this.user?.groups ?? []).includes('ADMIN')) {
          return this.confirmDuplication();
        } else {
          this.toastService.show(
            "L'individu que vous voulez modifier existe déjà. Veuillez contacter votre administrateur pour continuer ou corriger votre saisie.",
            'danger',
          );
          return false;
        }
      } else {
        this.toastService.show('Une erreur serveur est survenue, merci de réessayer plus tard.', 'danger');
        return false;
      }
    }
  }

  private async confirmDuplication(): Promise<boolean> {
    const modal: NgbModalRef = this.modalService.open(ConfirmationComponent, {
      backdrop: 'static',
      centered: true,
    });
    const modalComponent: ConfirmationComponent = modal.componentInstance as ConfirmationComponent;
    return modalComponent.confirm("L'individu que vous voulez modifier existe déjà. Êtes-vous sûr de vouloir créer ce doublon ?");
  }

  private normalizeNames() {
    this.personalDataForm.controls.usualName.setValue(this.personalDataForm.controls.usualName.value?.toUpperCase()?.trim());
    this.personalDataForm.controls.surname.setValue(this.personalDataForm.controls.surname.value?.toUpperCase()?.trim());
    this.personalDataForm.controls.firstName.setValue(Utils.capitalizeFirstLetters(this.personalDataForm.controls.firstName.value));
    this.personalDataForm.controls.secondName.setValue(Utils.capitalizeFirstLetters(this.personalDataForm.controls.secondName.value));
  }

  private markNamesAndBirthDateAsPristine() {
    this.personalDataForm.controls.usualName.markAsPristine();
    this.personalDataForm.controls.firstName.markAsPristine();
    this.personalDataForm.controls.birthDate.markAsPristine();
  }

  public userIsInStructureGroup(): boolean {
    return this.user?.groups?.includes('STRUCTURE') ?? false;
  }

  public getCliLabel(cliId: number): string {
    const cli = this.clis.find((x) => x.id === cliId);
    return cli ? cli.label : '';
  }

  public getCtmLabel(ctmId: number): string {
    const ctm = this.ctms.find((x) => x.id === ctmId);
    return ctm ? ctm.label : '';
  }
}
